import { useEffect, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import { getCourseAdminPermissions } from 'shared-helpers/courses';
import { localeLanguageCodes } from 'shared-locales';
import api from 'api';
import { breakpoints } from 'styles';
import {
  ApiPostButton,
  Badge,
  DisplayFormattedDate,
  EmptyIndicator,
  MoreOptions,
  Owners,
  Pagination,
  SendToEmailButton
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { Icon } from 'components/common/icons';
import { CourseProgressStatus } from 'components/common/tasks';
import {
  CourseUserDeadline,
  CourseUserMandatory,
  CoursePathDeadlines,
  CourseTitleWithInfo,
  CourseViewVersion
} from 'components/courses/common';
import { FollowUsersDownloadAnswers } from 'components/courses/follow';
import { UsersCompletionFilter } from 'components/users';
import { ProfileCoursesTypeFilter } from '.';

import type { PageState } from 'types';

interface Props {
  isUserPage?: boolean;
}

type CourseType = 'default' | 'accessible' | 'administered';

interface State {
  courses: PageState['course'][];
  loading: boolean;
  pages: number;
  page: number;
  type: CourseType;
}

const ProfileCourses = ({ isUserPage }: Props) => {
  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: { completionStatus = 'all', user },
    serverConfig,
    theme,
    user: loggedInUser
  } = useReduxState();

  const [state, setState] = useState<State>({
    courses: null,
    loading: true,
    page: 1,
    pages: 0,
    type: 'default'
  });

  const updateCourses = async ({
    completion,
    page,
    type
  }: {
    completion?: PageState['completionStatus'];
    page?: number;
    type?: CourseType;
  }) => {
    try {
      setState({ ...state, loading: true });

      const completionValue = completion || completionStatus;
      const pageValue = page || 1;
      const typeValue = type !== undefined ? type : state.type;
      const apiUrls = {
        default: `/admin/users/${user.id}/courses`,
        accessible: `/admin/users/${user.id}/accessible-courses`,
        administered: `/admin/users/${user.id}/administered-courses`
      };

      const courses = await api.get(
        `${apiUrls[typeValue]}?page=${pageValue}${
          typeValue === 'default' ? `&completionStatus=${completionValue}` : ''
        }`
      );

      setState({
        ...state,
        courses: courses.data.courses,
        loading: false,
        pages: courses.data.pages,
        page: pageValue,
        type: typeValue
      });
    } catch (err) {
      setState({ ...state, loading: false });
      await dispatch.handleErrorDispatch(err);
    }
  };

  const updateCompletionStatus = async ({ completionStatus }) => {
    await updateCourses({
      completion: completionStatus,
      page: 1,
      type: state.type
    });
  };

  useEffect(() => {
    updateCourses({});
  }, []);

  const { courses, loading, page, pages, type } = state;

  const permissionOptions = getCourseAdminPermissions({ messages });

  const accessOptions = {
    added: messages.commonCourse,
    admin: messages.usersCourseAdmin,
    available: messages.commonAvailable,
    group: messages.commonGroup,
    'group-or-path': messages.profileGroupOrPath,
    path: messages.path,
    owner: messages.usersCourseOwner
  };

  return (
    <div className="summary">
      <Box
        headerContent={
          <div style={{ display: 'flex', gap: 10 }}>
            <UsersCompletionFilter
              disabled={state.type !== 'default'}
              updateUsers={updateCompletionStatus}
              view="course"
            />

            {isUserPage &&
            loggedInUser &&
            loggedInUser.roles.includes('superadmin') ? (
              <ProfileCoursesTypeFilter
                type={type}
                updateCourses={updateCourses}
              />
            ) : null}
          </div>
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        title={messages.commonCourses}
      >
        {courses && courses.length && type === 'default' ? (
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
              <BoxTableCell>{messages.commonCourse}</BoxTableCell>
              <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                {messages.courseLastOpened}
              </BoxTableCell>
              <BoxTableCell>{messages.commonMandatory}</BoxTableCell>
              <BoxTableCell>{messages.courseTargetSchedule}</BoxTableCell>
              <BoxTableCell>{messages.commonStatus}</BoxTableCell>
              <BoxTableCell></BoxTableCell>
            </BoxTableHeader>

            {courses.map((course) => (
              <BoxTableRow key={course.id}>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  <CourseTitleWithInfo
                    course={course}
                    linkAs={
                      isUserPage
                        ? `/users/${user.id}/courses/${course.id}`
                        : `/profile/courses/${course.id}`
                    }
                    linkHref={
                      isUserPage
                        ? '/users/[userId]/courses/[courseId]'
                        : '/profile/courses/[courseId]'
                    }
                    preferredLanguageVersion={
                      isUserPage
                        ? undefined
                        : course.progressData?.completion
                            ?.courseLanguageVersion ||
                          localeLanguageCodes[language]
                    }
                    style={
                      serverConfig.productHideOwner
                        ? { alignItems: 'center' }
                        : {}
                    }
                  >
                    {!serverConfig.productHideOwner && (
                      <Owners owners={course.owners} />
                    )}
                  </CourseTitleWithInfo>
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.large}
                  icon={course.lastOpenedDate ? ['far', 'calendar-alt'] : null}
                  label={messages.courseLastOpened}
                >
                  {course.lastOpenedDate ? (
                    <DisplayFormattedDate
                      date={course.lastOpenedDate}
                      withTime={serverConfig.analyticsDatesDisplayTime}
                    />
                  ) : (
                    <span
                      style={{ color: theme.textColorLighter, fontWeight: 400 }}
                    >
                      {messages.commonNever}

                      {course.progressData?.analyticsDisabledForAdmin ? (
                        <span
                          aria-label={messages.tasksIgnoredInfo}
                          data-tooltip={messages.tasksIgnoredInfo}
                          style={{
                            fontSize: '1rem',
                            color: theme.textColorLighter,
                            marginLeft: 5,
                            position: 'relative',
                            top: -1
                          }}
                        >
                          <Icon icon="exclamation-triangle" />
                        </span>
                      ) : null}
                    </span>
                  )}
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.large}
                  label={messages.commonMandatory}
                >
                  <CourseUserMandatory
                    mandatoryReasons={course.mandatoryReasons}
                    showCourseReason={true}
                  />
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.large}
                  label={messages.commonAdded}
                >
                  {course.deadline ? (
                    <CourseUserDeadline
                      deadline={course.deadline}
                      isActive={
                        course.progressData?.completion?.status !== 'success'
                      }
                      showIcon={true}
                    />
                  ) : course.relatedPathDeadlines?.length ? (
                    <CoursePathDeadlines
                      courseId={course.id}
                      isActive={
                        course.progressData?.completion?.status !== 'success'
                      }
                      pathDeadlines={course.relatedPathDeadlines}
                      userId={user.id}
                    />
                  ) : null}
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.large}
                  label={messages.commonStatus}
                >
                  <CourseProgressStatus
                    course={course}
                    id={`profile-course-progress-status-${course.id}`}
                    progressData={course.progressData}
                  />
                </BoxTableCell>

                <BoxTableCell shrink={true}>
                  {isUserPage ||
                  course.tasksAllowDownloading ||
                  course.progressData?.completion?.courseVersion ? (
                    <MoreOptions styleContentButtons={false}>
                      {course.progressData?.completion?.courseVersion ? (
                        <CourseViewVersion
                          completionId={course.progressData.completion.id}
                          courseSlug={course.slug}
                          courseVersion={
                            course.progressData.completion
                              .courseVersion as string
                          }
                        />
                      ) : null}

                      {course.accessToTaskData ? (
                        <>
                          <FollowUsersDownloadAnswers
                            completionId={course.progressData?.completion?.id}
                            courseId={course.id}
                            includeReviews={course.tasksIncludeReviews}
                            isAdmin={Boolean(isUserPage)}
                            isDropdown={true}
                            user={user}
                          />
                          {loggedInUser?.email ? (
                            <ApiPostButton
                              data={{ language }}
                              icon="envelope"
                              isDropdown={true}
                              successMessage={messages.tasksSendAnswersSuccess}
                              text={messages.tasksSendAnswers}
                              url={`/admin/users/${user.id}/courses/${course.id}/answers/send`}
                            />
                          ) : (
                            <SendToEmailButton
                              apiData={{ language }}
                              apiUrl={`/admin/users/${user.id}/courses/${course.id}/answers/send`}
                              buttonText={messages.tasksSendAnswers}
                              id="answers"
                              isDropdown={true}
                              successMessage={messages.tasksSendAnswersSuccess}
                            />
                          )}
                        </>
                      ) : null}
                    </MoreOptions>
                  ) : null}
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : courses && courses.length && type === 'accessible' ? (
          <BoxTable>
            <BoxTableHeader>
              <BoxTableCell>{messages.commonCourse}</BoxTableCell>
              <BoxTableCell>{messages.commonAccess}</BoxTableCell>
            </BoxTableHeader>
            {courses.map((course) => (
              <BoxTableRow key={course.id}>
                <BoxTableCell>
                  <CourseTitleWithInfo
                    course={course}
                    linkToCourseAdminPage={true}
                  >
                    <Owners owners={course.owners} />
                  </CourseTitleWithInfo>
                </BoxTableCell>
                <BoxTableCell>{accessOptions[course.relation]}</BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : courses && courses.length && type === 'administered' ? (
          <BoxTable>
            <BoxTableHeader>
              <BoxTableCell>{messages.commonCourse}</BoxTableCell>
              <BoxTableCell>{messages.usersPermissions}</BoxTableCell>
            </BoxTableHeader>
            {courses.map((course) => (
              <BoxTableRow key={course.id}>
                <BoxTableCell>
                  <CourseTitleWithInfo
                    course={course}
                    linkToCourseAdminPage={true}
                  >
                    <Owners owners={course.owners} />
                  </CourseTitleWithInfo>
                </BoxTableCell>
                <BoxTableCell>
                  {course.isOwner ? (
                    <Badge
                      icon="key"
                      style={{ margin: '2px 10px 2px 0' }}
                      text={messages.commonOwner}
                    />
                  ) : (
                    course.permissions.map((permission) => (
                      <Badge
                        key={permission}
                        style={{ margin: '2px 10px 2px 0' }}
                        text={permissionOptions[permission]}
                      />
                    ))
                  )}
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : (
          <EmptyIndicator
            description={messages.profileRecentCoursesEmptyInfo}
            icon="book"
            title={messages.coursesNoCoursesFound}
          />
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={pages}
        onPageChange={updateCourses}
      />
    </div>
  );
};

export default ProfileCourses;
